// src/components/BoardTheme/BoardTheme.js

import React, { useRef, useState, useEffect } from 'react';
import ErrorBoundary from './ErrorBoundary';
import Toolbar from './theme/Toolbar';
import CanvasArea from './theme/CanvasArea';
import { Panel, PanelGroup } from "react-resizable-panels";
import GlobalSettingsPanel from './theme/GlobalSettingsPanel';
import ResizeHandle from "./ResizeHandle.js";
import ThemeGrid from './theme/ThemeGrid.js';
import {
  ThemeBar,
  ThemeLine,
  ThemeValue,
  ThemeText,
  ThemeTime,
  ThemeImageIndicator,
  ThemeRecIndicator,
  ThemeSpecialIndicator,
  ThemeSpeedLimiterIndicator,
  ThemeNavMap,
  ThemeNavCompass,
  ThemeNavInstruction,
  ThemeNavDirection,
  ThemeImageCustom,
  SettingsComponents,
} from './theme/elements'; // Import all theme components and settings
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import debounce from 'lodash.debounce';
import { getThemeTypeLabelFromValue } from './theme/ThemeLists.js';
import DatabaseService from "../services/database.service.js";
import AuthService from '../services/auth.service.js';
import { SCREEN_HEIGHT, SCREEN_WIDTH } from './theme/ScreenProps';

const LOCAL_STORAGE_CURRENT_DATA_KEY = 'themeCurrentData';
const LOCAL_STORAGE_CURRENT_THEME_ID_KEY = 'themeCurrentId';
const LOCAL_STORAGE_CURRENT_THEME_OWNER_KEY = 'themeCurrentOwner';


const BoardTheme = () => {
  // References
  const stageRef = useRef(null);
  const fileInputRef = useRef(null); // Reference for hidden file input
  const canvasContainerRef = useRef(null); // Reference to the canvas container for screenshot

  const currentUser = AuthService.getCurrentUser();
  const isAdmin = currentUser.roles.includes("admin_smart")

  // State Variables
  const [elements, setElements] = useState([]);
  const [selectedElement, setSelectedElement] = useState(null);
  const [backgroundColor, setBackgroundColor] = useState('#000000'); // Original background color
  const [isInitialized, setIsInitialized] = useState(false);
  const [debugOverlaps, setDebugOverlaps] = useState(false); // Debug mode
  const [debugRandom, setDebugRandom] = useState(false);
  const [themeName, setThemeName] = useState('');

  const [editAllowed, setEditAllowed] = useState(false);

  const [themeId, setThemeId] = useState(-1);
  const themeIdRef = useRef(themeId);

  const [currentThemeOwnerEmail, setCurrentThemeOwnerEmail] = useState('');

  const [themesList, setThemesList] = useState([]);

  const [debugRendering, setDebugRendering] = useState(true); // Controls grid and anchors visibility
  const toggleDebugRendering = () => {
    setDebugRendering(!debugRendering);
  }

  const [clipboard, setClipboard] = useState(null); // Holds the copied element
  const [undoStack, setUndoStack] = useState([]); // For undo functionality

  const [isSavingToDatabase, setIsSavingToDatabase] = useState(false);
  const [isSavingSnapshotLocal, setIsSavingSnapshotLocal] = useState(false);

  const [snapToGridEnabled, setSnapToGridEnabled] = useState(true); // Snapping to grid

  const [scale, setScale] = useState(2); // Adjusted scale for accurate screenshot
  const [gridSize, setGridSize] = useState(10); // Grid size for snapping

  // Grid customization state variables (optional)
  const [gridColor, setGridColor] = useState('#444'); // Grid line color
  const [gridLineWidth, setGridLineWidth] = useState(0.5); // Grid line thickness
  const [gridOpacity, setGridOpacity] = useState(1); // Grid line opacity

  // Dynamic grid based on Stage size
  const [stageSize, setStageSize] = useState({ width: SCREEN_WIDTH, height: SCREEN_HEIGHT }); // Fixed size for consistency

  // Global Settings State
  const [mainColor, setMainColor] = useState('#ffffff'); // Default main color
  const [distanceUnit, setDistanceUnit] = useState('metric'); // 'metric' or 'imperial'
  const [temperatureUnit, setTemperatureUnit] = useState('celsius'); // 'celsius' or 'fahrenheit'
  const [themeType, setThemeType] = useState('ride'); // Default theme type

  // Brake State
  const [isBrakeActive, setIsBrakeActive] = useState(false);

  // New State Variable for Snapshot Process
  const [isTakingSnapshot, setIsTakingSnapshot] = useState(false);

  useEffect(() => {
    themeIdRef.current = themeId;
  }, [themeId]);

  // Update stageSize on window resize
  useEffect(() => {
    const updateStageSize = () => {
      const container = canvasContainerRef.current;
      if (container) {
        const rect = container.getBoundingClientRect();
        setStageSize({
          width: SCREEN_WIDTH, // Fixed width
          height: SCREEN_HEIGHT, // Fixed height
        });
      }
    };

    updateStageSize(); // Initial size
    // window.addEventListener('resize', updateStageSize);
    // return () => window.removeEventListener('resize', updateStageSize);
  }, []);

  useEffect(() => {
    window.addEventListener('error', e => {
      if ((e.message === 'ResizeObserver loop limit exceeded') || (e.message === 'ResizeObserver loop completed with undelivered notifications.')) {
        const resizeObserverErrDiv = document.getElementById(
          'webpack-dev-server-client-overlay-div'
        );
        const resizeObserverErr = document.getElementById(
          'webpack-dev-server-client-overlay'
        );
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute('style', 'display: none');
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute('style', 'display: none');
        }
      }
    });
  }, []);


  const loadThemes = () => {
    DatabaseService.getThemes(isAdmin)
      .then((response) => {

        // filter themes with owner email different from user email which are not shared
        const filteredList = response.data.filter((theme) => {
          const isVisible = theme.owner_email === currentUser.email || theme.shared === true || isAdmin;
          // console.log("theme.owner_email", theme.owner_email, "currentUser.email", currentUser.email, "isVisible", isVisible);
          return isVisible;
        });


        setThemesList(filteredList);
      })
      .catch((error) => {
        console.log("Themes could not be loaded", error);
      });
  };


  const deleteTheme = (id) => {
    if (window.confirm("Are you sure you want to delete this theme?")) {
      if (window.confirm("Are you sure REALLY SURE you want to delete this theme? This is irreversible.")) {
        DatabaseService.removeTheme(id).then((response) => {
          loadThemes();
        })
      }
    }
  }

  const shareTheme = (id, shared) => {
    DatabaseService.shareTheme(id, shared).then((response) => {
      loadThemes();
      if (shared)
        toast.success('Your theme is now shared!');
      else
        toast.success('Your theme is now private!');
    })
  }

  const saveTheme = () => {
    setIsSavingToDatabase(true);
    setIsSavingSnapshotLocal(false);
    setIsTakingSnapshot(true);
    setDebugRendering(false);
  }

  const cloneTheme = (id) => {
    DatabaseService.getThemeData(id).then((response) => {
      loadThemeDataFromJSON(id, AuthService.getCurrentUser().email, response.data[0].data);
      setThemeId(-1);
      setCurrentThemeOwnerEmail(AuthService.getCurrentUser().email);
      setEditAllowed(true)
      setIsSavingToDatabase(true);
      setIsTakingSnapshot(true);
      setDebugRendering(false);
      })
  }

  useEffect(() => {
    loadThemes()

  }, []);

  // Handle snapping to the nearest grid
  const snapToGrid = (value) => Math.round(value / gridSize) * gridSize;


  // Function to update an element's properties and synchronize selectedElement
  const updateElement = (id, property, value) => {
    setElements((prevElements) =>
      prevElements.map((el) => {
        if (el.id === id) {
          const updatedEl = { ...el, [property]: value };
          if (selectedElement && el.id === selectedElement.id) {
            setSelectedElement(updatedEl);
          }
          return updatedEl;
        }
        return el;
      })
    );
  };

  // Handle drop events
  const handleDrop = (e) => {
    e.preventDefault();

    const type = e.dataTransfer.getData('text/plain');
    console.log('Dropped type:', type); // Debugging

    if (!type) {
      console.error('No type data found in drop');
      toast.error('Failed to drop element: No type specified.');
      return;
    }

    const stageComponent = stageRef.current;

    if (!stageComponent) {
      console.error('Stage component is null');
      toast.error('Failed to drop element: Stage component is not available.');
      return;
    }

    const stage = stageComponent.getStage();

    if (!stage) {
      console.error('Konva Stage is null');
      toast.error('Failed to drop element: Konva Stage is not available.');
      return;
    }

    // Calculate the drop position relative to the Stage's container
    const container = stage.container();
    const rect = container.getBoundingClientRect();

    // Get the client's mouse position
    const clientX = e.clientX;
    const clientY = e.clientY;

    // Calculate the position within the Stage
    let pointerPosition = {
      x: (clientX - rect.left) / scale,
      y: (clientY - rect.top) / scale,
    };

    if (pointerPosition.x < 0) pointerPosition.x = 0;
    if (pointerPosition.y < 0) pointerPosition.y = 0;
    if (pointerPosition.x > 479) pointerPosition.x = 479;
    if (pointerPosition.y > 319) pointerPosition.y = 319;

    // Apply snapping if enabled
    if (snapToGridEnabled) {
      pointerPosition = {
        x: snapToGrid(pointerPosition.x),
        y: snapToGrid(pointerPosition.y),
      };
    } else {
      pointerPosition = {
        x: parseInt(pointerPosition.x),
        y: parseInt(pointerPosition.y),
      };
    }

    const position = {
      x: pointerPosition.x,
      y: pointerPosition.y,
    };

    console.log('New Element Position:', position); // Debugging

    let newElement = {
      id: `${type}-${Date.now()}`, // Unique ID
      type,
      ...position,
      usage: 'None',
      size: 10,
      color: '#ffffff', // Original element color
    };

    // Customize properties based on type
    switch (type) {
      case 'value':
        newElement = {
          ...newElement,
          digitsBefore: 2,
          digitsAfter: 0,
          testValue: '0',
          size: 10,
          color: '#ffffff',
          usage: '0',          // Initialize usage to '0' (None)
          brakeEnabled: false, // Initialize brakeEnabled to false
          brakeUsage: '0',     // Initialize brakeUsage to '0' (None)
          brakeDigitsBefore: 2,
          brakeDigitsAfter: 0,
          brakeTestValue: '0',
        };
        break;
      case 'time':
        // Initialize with current time
        const now = new Date();
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        newElement = {
          ...newElement,
          text: `${hours}:${minutes}`,
          size: 10,
          color: '#ffffff',
        };
        break;
      case 'barGraph':
        newElement = {
          ...newElement,
          barWidth: 5,
          barHeight: 20,
          margin: 2,
          numberOfBars: 20,
          startColor: '#00ff00',
          endColor: '#ff0000',
          disabledColor: '#777777',
          startValue: 0,
          endValue: 100,
          testValue: 80,
          rotation: 0,
        };
        break;
      case 'line':
        newElement = {
          ...newElement,
          points: [position.x, position.y, position.x + 50, position.y + 50],
          strokeWidth: 1, // Default stroke width
        };
        break;
      case 'text':
        newElement = {
          ...newElement,
          text: 'Edit Me',
          brakeEnabled: false, // Default is brake disabled
          brakeText: '', // Default brake text is empty
          textAlign: 0, // Default text alignment
          unitType: 0, // Initialize to 'None'
          brakeUnitType: 0
        };
        break;
      case 'imageIndicator':
        newElement = {
          ...newElement,
          usage: 0, // Default usage
          width: 100, // Default width
          height: 100, // Default height
          colorEnabled: '#ffffff', // Original element color
          colorDisabled: '#9b9b9b', // Original element color
        };
        break;
      case 'recIndicator':
        newElement = {
          ...newElement,
          size: 6, // Default radius
          color: '#ff0000', // Default color (e.g., red for recording)
        };
        break;
      case 'specialIndicator':
        newElement = {
          ...newElement,
          usage: 0, // Default usage
          size: 10, // Default size
          color: '#FFFFFF', // Default color
        };
        break;
      case 'speedLimiterIndicator':
        newElement = {
          ...newElement,
          size: 15, // Default size
          color: '#FFFFFF', // Default color (white)
          corner: 'bottom-left', // Default corner orientation
        };
        break;
      case 'navMap':
        newElement = {
          ...newElement,
          roadLineColor: '#ffffff',
          selectedRoadColor: '#ccccff',
          startPointColor: '#00ff00',
          destinationPointColor: '#ff0000',
          reserved1Color: '#ff0000',
          reserved2Color: '#ffffff',
          reserved3Color: '#ffffff',
        };
        break;
      case 'navDirection':
        newElement = {
          ...newElement,
          color: '#ffffff',
        };
        break;
      case 'navCompass':
        newElement = {
          ...newElement,
          arrowColor: '#ffffff',
          ringColor: '#ffffff',
        };
        break;
      case 'navInstruction':
        newElement = {
          ...newElement,
          textColor: '#ffffff',
          size: 10,
        };
        break;
      case 'imageCustom':
        newElement = {
          ...newElement,
        };
        break;

      default:
        console.error(`Unknown type: ${type}`);
        toast.error(`Unknown element type: ${type}`);
        return;
    }

    console.log('New Element:', newElement); // Debugging

    setElements((prevElements) => [...prevElements, newElement]);
    setSelectedElement(newElement);
    toast.success('Element added successfully!');
  };

  // Handle drag over events
  const handleDragOver = (e) => {
    e.preventDefault(); // Necessary to allow a drop
    e.dataTransfer.dropEffect = 'copyMove';
    // console.log('Drag over', e);
  };

  // Delete selected element
  const deleteElement = () => {
    if (selectedElement) {
      setElements((prevElements) => prevElements.filter((el) => el.id !== selectedElement.id));
      // Push the deleted element to the undo stack
      setUndoStack((prevStack) => [...prevStack, selectedElement]);
      setSelectedElement(null);
      toast.info('Element deleted. Press CTRL+Z to undo.');
    }
  };

  // Copy selected element
  const copyElement = (element) => {
    // Deep copy to avoid reference issues
    element.originalThemeId = themeId;
    const elementCopy = JSON.parse(JSON.stringify(element));
    setClipboard(elementCopy);
    toast.info('Element copied.');
  };

  // Paste element with offset
  const pasteElement = (element) => {
    // Create a new unique ID
    const newId = `${element.type}-${Date.now()}`;

    console.log("element.originalThemeId:", element.originalThemeId, "themeId", themeId);

    // Create a new element with an offset and snap to grid
    const newElement = {
      ...element,
      id: newId,
      x: (element.originalThemeId == themeId) ? snapToGrid(element.x + 10) : element.x, // Offset X by 10 pixels and snap to grid
      y: (element.originalThemeId == themeId) ? snapToGrid(element.y + 10) : element.y, // Offset Y by 10 pixels and snap to grid
    };

    setElements((prevElements) => [...prevElements, newElement]);
    setSelectedElement(newElement); // Optionally, select the new element
    toast.success('Element pasted.');
    console.log('Element pasted:', newElement); // Debugging
  };

  // Render different types of elements
  const renderElement = (el) => {
    return (
      <React.Fragment key={el.id}>
        {(() => {
          switch (el.type) {
            case 'value':
              return (
                <ThemeValue
                  key={el.id}
                  element={el}
                  gridSize={gridSize}
                  selectedElement={selectedElement}
                  setSelectedElement={setSelectedElement}
                  updateElement={updateElement}
                  isBrakeActive={isBrakeActive}
                  snapToGridEnabled={snapToGridEnabled}
                  render={!debugRendering} editAllowed={editAllowed}
                  scale={scale}
                  screenWidth={SCREEN_WIDTH}
                  screenHeight={SCREEN_HEIGHT}
                />
              );
            case 'time':
              return (
                <ThemeTime
                  key={el.id}
                  element={el}
                  gridSize={gridSize}
                  selectedElement={selectedElement}
                  setSelectedElement={setSelectedElement}
                  updateElement={updateElement}
                  snapToGridEnabled={snapToGridEnabled}
                  render={!debugRendering} editAllowed={editAllowed}
                  scale={scale}
                  screenWidth={SCREEN_WIDTH}
                  screenHeight={SCREEN_HEIGHT}
                />
              );
            case 'barGraph':
              return (
                <ThemeBar
                  key={el.id}
                  element={el}
                  gridSize={gridSize}
                  selectedElement={selectedElement}
                  setSelectedElement={setSelectedElement}
                  updateElement={updateElement}
                  snapToGridEnabled={snapToGridEnabled}
                  render={!debugRendering} editAllowed={editAllowed}
                  scale={scale}
                  screenWidth={SCREEN_WIDTH}
                  screenHeight={SCREEN_HEIGHT}
                />
              );
            case 'line':
              return (
                <ThemeLine
                  key={el.id}
                  element={el}
                  gridSize={gridSize}
                  selectedElement={selectedElement}
                  setSelectedElement={setSelectedElement}
                  updateElement={updateElement}
                  snapToGridEnabled={snapToGridEnabled}
                  render={!debugRendering} editAllowed={editAllowed}
                  scale={scale}
                  screenWidth={SCREEN_WIDTH}
                  screenHeight={SCREEN_HEIGHT}
                />
              );
            case 'text':
              return (
                <ThemeText
                  key={el.id}
                  element={el}
                  gridSize={gridSize}
                  selectedElement={selectedElement}
                  setSelectedElement={setSelectedElement}
                  updateElement={updateElement}
                  isBrakeActive={isBrakeActive}
                  snapToGridEnabled={snapToGridEnabled}
                  distanceUnit={distanceUnit}
                  temperatureUnit={temperatureUnit}
                  render={!debugRendering} editAllowed={editAllowed}
                  scale={scale}
                  screenWidth={SCREEN_WIDTH}
                  screenHeight={SCREEN_HEIGHT}
                />
              );
            case 'imageIndicator':
              return (
                <ThemeImageIndicator
                  key={el.id}
                  element={el}
                  selectedElement={selectedElement}
                  setSelectedElement={setSelectedElement}
                  updateElement={updateElement}
                  snapToGridEnabled={snapToGridEnabled}
                  render={!debugRendering} editAllowed={editAllowed}
                  scale={scale}
                  screenWidth={SCREEN_WIDTH}
                  screenHeight={SCREEN_HEIGHT}
                  gridSize={gridSize}
                />
              );
            case 'recIndicator':
              return (
                <ThemeRecIndicator
                  key={el.id}
                  element={el}
                  gridSize={gridSize}
                  selectedElement={selectedElement}
                  setSelectedElement={setSelectedElement}
                  updateElement={updateElement}
                  snapToGridEnabled={snapToGridEnabled}
                  render={!debugRendering} editAllowed={editAllowed}
                  scale={scale}
                  screenWidth={SCREEN_WIDTH}
                  screenHeight={SCREEN_HEIGHT}
                />
              );
            case 'specialIndicator':
              return (
                <ThemeSpecialIndicator
                  key={el.id}
                  element={el}
                  gridSize={gridSize}
                  selectedElement={selectedElement}
                  setSelectedElement={setSelectedElement}
                  updateElement={updateElement}
                  snapToGridEnabled={snapToGridEnabled}
                  render={!debugRendering} editAllowed={editAllowed}
                  scale={scale}
                  screenWidth={SCREEN_WIDTH}
                  screenHeight={SCREEN_HEIGHT}
                />
              );
            case 'speedLimiterIndicator':
              return (
                <ThemeSpeedLimiterIndicator
                  key={el.id}
                  element={el}
                  gridSize={gridSize}
                  selectedElement={selectedElement}
                  setSelectedElement={setSelectedElement}
                  updateElement={updateElement}
                  snapToGridEnabled={snapToGridEnabled}
                  render={!debugRendering} editAllowed={editAllowed}
                  scale={scale}
                  screenWidth={SCREEN_WIDTH}
                  screenHeight={SCREEN_HEIGHT}
                />
              );
            case 'navMap':
              return (
                <ThemeNavMap
                  key={el.id}
                  element={el}
                  gridSize={gridSize}
                  selectedElement={selectedElement}
                  setSelectedElement={setSelectedElement}
                  updateElement={updateElement}
                  snapToGridEnabled={snapToGridEnabled}
                  render={!debugRendering} editAllowed={editAllowed}
                  scale={scale}
                  screenWidth={SCREEN_WIDTH}
                  screenHeight={SCREEN_HEIGHT}
                />
              );
            case 'navDirection':
              return (
                <ThemeNavDirection
                  key={el.id}
                  element={el}
                  gridSize={gridSize}
                  selectedElement={selectedElement}
                  setSelectedElement={setSelectedElement}
                  updateElement={updateElement}
                  snapToGridEnabled={snapToGridEnabled}
                  render={!debugRendering} editAllowed={editAllowed}
                  scale={scale}
                  screenWidth={SCREEN_WIDTH}
                  screenHeight={SCREEN_HEIGHT}
                />
              );
            case 'navCompass':
              return (
                <ThemeNavCompass
                  key={el.id}
                  element={el}
                  gridSize={gridSize}
                  selectedElement={selectedElement}
                  setSelectedElement={setSelectedElement}
                  updateElement={updateElement}
                  snapToGridEnabled={snapToGridEnabled}
                  render={!debugRendering}
                  editAllowed={editAllowed}
                  scale={scale}
                  screenWidth={SCREEN_WIDTH}
                  screenHeight={SCREEN_HEIGHT}
                />
              );

            case 'navInstruction':
              return (
                <ThemeNavInstruction
                  key={el.id}
                  element={el}
                  gridSize={gridSize}
                  selectedElement={selectedElement}
                  setSelectedElement={setSelectedElement}
                  updateElement={updateElement}
                  snapToGridEnabled={snapToGridEnabled}
                  render={!debugRendering} editAllowed={editAllowed}
                  scale={scale}
                  screenWidth={SCREEN_WIDTH}
                  screenHeight={SCREEN_HEIGHT}
                />
              );

            case 'imageCustom':
              return (
                <ThemeImageCustom
                  key={el.id}
                  element={el}
                  selectedElement={selectedElement}
                  setSelectedElement={setSelectedElement}
                  updateElement={updateElement}
                  snapToGridEnabled={snapToGridEnabled}
                  render={!debugRendering} editAllowed={editAllowed}
                  scale={scale}
                  screenWidth={SCREEN_WIDTH}
                  screenHeight={SCREEN_HEIGHT}
                  gridSize={gridSize}
                />
              );

            default:
              return null;
          }
        })()}
      </React.Fragment>
    );
  };

  // Implement Export Theme (previously handleSaveTheme)
  const handleExportTheme = () => {
    const ThemeData = {
      themeName,
      elements,
      backgroundColor,
      debugOverlaps,
      debugRandom,
      snapToGridEnabled, // Include snapping state
      gridSize, // Include gridSize
      scale, // Include scale
      mainColor, // Include main color
      distanceUnit, // Include distance unit
      temperatureUnit, // Include temperature unit
      themeType, // Include theme type
      // Include other global settings here if needed
    };

    const json = JSON.stringify(ThemeData, null, 2); // Pretty-print with 2 spaces
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = themeId + " - " + getThemeTypeLabelFromValue(themeType) + " - " + themeName + ".json";
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success('Theme exported successfully!');
  };

  const loadThemeData = (id, ownerEmail) => {
    DatabaseService.getThemeData(id).then((response) => {
      loadThemeDataFromJSON(id, ownerEmail, response.data[0].data);
    })
  }

  const loadThemeDataFromJSON = (id, ownerEmail, data) => {
    // Optional: Validate each element's structure
    const validTypes = [
      'value',
      'barGraph',
      'line',
      'text',
      'time',
      'imageIndicator',
      'recIndicator',
      'specialIndicator',
      'speedLimiterIndicator',
      'navMap',
      'navDirection',
      'navCompass',
      'navInstruction',
      'imageCustom',
    ];
    data.elements.forEach((el, index) => {
      if (
        !el.id ||
        !el.type ||
        !validTypes.includes(el.type) ||
        typeof el.x !== 'number' ||
        typeof el.y !== 'number'
      ) {
        throw new Error(`Invalid element structure at index ${index}.`);
      }
    });

    // Update state
    setElements(data.elements);


    if (data.themeName) {
      setThemeName(data.themeName);
    }
    if (data.backgroundColor) {
      setBackgroundColor(data.backgroundColor);
    }
    if (typeof data.snapToGridEnabled === 'boolean') {
      setSnapToGridEnabled(data.snapToGridEnabled);
    }
    if (typeof data.gridSize === 'number') {
      setGridSize(data.gridSize);
    }

    // Update global settings
    if (data.mainColor) {
      setMainColor(data.mainColor);
    }
    if (data.distanceUnit) {
      setDistanceUnit(data.distanceUnit);
    }
    if (data.temperatureUnit) {
      setTemperatureUnit(data.temperatureUnit);
    }
    if (data.themeType) {
      setThemeType(data.themeType);
    }
    if (typeof data.debugOverlaps === 'boolean') {
      setDebugOverlaps(data.debugOverlaps);
    }
    if (typeof data.debugRandom === 'boolean') {
      setDebugRandom(data.debugRandom);
    }

    setThemeId(id);
    // setCurrentThemeOwnerEmail(ownerEmail);
    setEditAllowed(currentUser.email == ownerEmail || isAdmin)
    console.log("loadThemeDataFromJSON / editAllowed ", currentUser.email == ownerEmail || isAdmin);

  }

  // Implement Import Theme (previously handleLoadTheme)
  const handleImportTheme = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = event.target.result;
        const data = JSON.parse(json);

        // Validate the structure
        if (!data.elements || !Array.isArray(data.elements)) {
          throw new Error('Invalid Theme format: Missing or incorrect "elements" array.');
        }
        loadThemeDataFromJSON(themeId, AuthService.getCurrentUser().email, data);

        // Optionally, clear the file input value to allow re-importing the same file
        e.target.value = '';

        toast.success('Theme imported successfully!');
        console.log('Theme imported:', data);
      } catch (error) {
        console.error('Error importing Theme:', error);
        toast.error(`Failed to import Theme: ${error.message}`);
      }
    };

    reader.onerror = () => {
      console.error('Error reading file.');
      toast.error('Failed to read the file.');
    };

    reader.readAsText(file);
  };


  // Implement Reset Canvas
  const createTheme = () => {
    const confirmReset = window.confirm('Are you sure you want to reset the canvas? This will remove all elements.');
    if (confirmReset) {
      setElements([]);
      setSelectedElement(null);
      setBackgroundColor('#000000'); // Reset to default background color
      setMainColor('#ffffff');
      setThemeName('new');
      setThemeId(-1);
      setThemeType(0);
      setCurrentThemeOwnerEmail(AuthService.getCurrentUser().email);
      setEditAllowed(true)

      setIsSavingToDatabase(true);
      setIsTakingSnapshot(true);
      setDebugRendering(false);
    }
  };

  // Implement Reset Canvas
  const handleResetCanvas = () => {
    const confirmReset = window.confirm('Are you sure you want to reset the canvas? This will remove all elements.');
    if (confirmReset) {
      setElements([]);
      setSelectedElement(null);
      setBackgroundColor('#000000'); // Reset to default background color
      setMainColor('#ffffff');
      setThemeName('new');
      setThemeId(-1);
      setThemeType(0);
      setCurrentThemeOwnerEmail(AuthService.getCurrentUser().email);

      // Reset other global settings if any
      toast.info('Canvas has been reset.');
    }
  };

  // Debounced function to save theme to localStorage
  const saveThemeToLocalStorage = useRef(
    debounce((themeData) => {
      try {
        console.log('Saving theme to localStorage... themeId: ', themeIdRef.current);
        localStorage.setItem(LOCAL_STORAGE_CURRENT_DATA_KEY, JSON.stringify(themeData));
        localStorage.setItem(LOCAL_STORAGE_CURRENT_THEME_ID_KEY, themeIdRef.current);
        localStorage.setItem(LOCAL_STORAGE_CURRENT_THEME_OWNER_KEY, currentThemeOwnerEmail);
        console.log('Theme saved to localStorage');
      } catch (error) {
        console.error('Error saving Theme to localStorage:', error);
        toast.error('Failed to save theme to localStorage.');
      }
    }, 1000) // 1 second debounce
  ).current;

  // Save to localStorage whenever relevant state changes
  useEffect(() => {
    if (isInitialized) {
      const ThemeData = {
        elements,
        themeName,
        backgroundColor,
        snapToGridEnabled,
        gridSize,
        scale,
        mainColor,
        distanceUnit,
        temperatureUnit,
        themeType,
        debugOverlaps,
        debugRandom,
        themeId,
      };
      saveThemeToLocalStorage(ThemeData);
    }
  }, [
    elements,
    themeName,
    backgroundColor,
    snapToGridEnabled,
    gridSize,
    scale,
    mainColor,
    distanceUnit,
    temperatureUnit,
    themeType,
    debugOverlaps,
    debugRandom,
    isInitialized,
    saveThemeToLocalStorage,
    themeId,
    currentThemeOwnerEmail
  ]);

  // Load from localStorage on component mount
  useEffect(() => {
    try {

      setThemeId(Number(localStorage.getItem(LOCAL_STORAGE_CURRENT_THEME_ID_KEY)) || -1);

      const ownerEmail = localStorage.getItem(LOCAL_STORAGE_CURRENT_THEME_OWNER_KEY) || currentUser.email
      setCurrentThemeOwnerEmail(ownerEmail);
      setEditAllowed(currentUser.email == ownerEmail || isAdmin)

      const savedTheme = localStorage.getItem(LOCAL_STORAGE_CURRENT_DATA_KEY);
      if (savedTheme) {
        const data = JSON.parse(savedTheme);
        if (data.elements && Array.isArray(data.elements)) {
          setElements(data.elements);
        }
        if (data.themeName) {
          setThemeName(data.themeName);
        }
        if (data.backgroundColor) {
          setBackgroundColor(data.backgroundColor);
        }
        if (typeof data.snapToGridEnabled === 'boolean') {
          setSnapToGridEnabled(data.snapToGridEnabled);
        }
        if (typeof data.gridSize === 'number') {
          setGridSize(data.gridSize);
        }
        if (typeof data.scale === 'number') {
          setScale(data.scale);
        }

        // Load global settings
        if (data.mainColor) {
          setMainColor(data.mainColor);
        }
        if (data.distanceUnit) {
          setDistanceUnit(data.distanceUnit);
        }
        if (data.temperatureUnit) {
          setTemperatureUnit(data.temperatureUnit);
        }
        if (data.themeType) {
          setThemeType(data.themeType);
        }
        if (typeof data.debugOverlaps === 'boolean') {
          setDebugOverlaps(data.debugOverlaps);
        }
        if (typeof data.debugRandom === 'boolean') {
          setDebugRandom(data.debugRandom);
        }

        console.log('Theme loaded from localStorage');
      }
      setIsInitialized(true); // Initialize even if no saved theme
    } catch (error) {
      console.error('Error loading Theme from localStorage:', error);
      toast.error('Failed to load theme from localStorage.');
      setIsInitialized(true); // Initialize to prevent blocking saves
    }
  }, []);

  // Handle Keyboard Shortcuts: DEL, CTRL+C, CTRL+V, Arrow Keys, CTRL+Z
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore if focused on input or textarea
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      // DELETE Key: Delete the selected element
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedElement) {
          deleteElement();
          e.preventDefault();
        }
      }

      // CTRL+C: Copy the selected element
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        if (selectedElement) {
          copyElement(selectedElement);
          e.preventDefault();
        }
      }

      // CTRL+V: Paste the copied element with an offset
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        if (clipboard) {
          pasteElement(clipboard);
          e.preventDefault();
        }
      }

      // CTRL+Z: Undo the last deletion
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        if (undoStack.length > 0) {
          const lastDeletedElement = undoStack[undoStack.length - 1];
          setElements((prevElements) => [...prevElements, lastDeletedElement]);
          setUndoStack((prevStack) => prevStack.slice(0, -1));
          toast.success('Undo: Element restored.');
          e.preventDefault();
        }
      }

      // Arrow Keys: Move the selected element
      const moveStep = snapToGridEnabled ? gridSize : 1; // Move by grid size or 1 pixel

      if (selectedElement) {
        let moved = false;
        let newX = selectedElement.x || 0;
        let newY = selectedElement.y || 0;
        let moveX = 0;
        let moveY = 0;

        switch (e.key) {
          case 'ArrowUp':
            moveY = -moveStep;
            newY = snapToGridEnabled ? snapToGrid(selectedElement.y + moveY) : selectedElement.y + moveY;
            moved = true;
            break;

          case 'ArrowDown':
            moveY = moveStep;
            newY = snapToGridEnabled ? snapToGrid(selectedElement.y + moveY) : selectedElement.y + moveY;
            moved = true;
            break;

          case 'ArrowLeft':
            moveX = -moveStep;
            newX = snapToGridEnabled ? snapToGrid(selectedElement.x + moveX) : selectedElement.x + moveX;
            moved = true;
            break;

          case 'ArrowRight':
            moveX = moveStep;
            newX = snapToGridEnabled ? snapToGrid(selectedElement.x + moveX) : selectedElement.x + moveX;
            moved = true;
            break;

          default:
            break;
        }

        if (moved) {
          if (selectedElement.type !== 'line') {
            updateElement(selectedElement.id, 'x', newX);
            updateElement(selectedElement.id, 'y', newY);
          } else {
            // For lines, adjust all points by moveX and moveY
            const newPoints = selectedElement.points.map((value, index) => {
              if (index % 2 === 0) {
                // x coordinate
                return value + moveX;
              } else {
                // y coordinate
                return value + moveY;
              }
            });
            updateElement(selectedElement.id, 'points', newPoints);
          }
          e.preventDefault();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedElement, clipboard, undoStack, snapToGridEnabled, gridSize]);

  // Handler for Brake Button
  const toggleBrake = () => {
    setIsBrakeActive((prev) => !prev);
    toast.info(`Brake press simulation is ${!isBrakeActive ? 'activated' : 'deactivated'}.`);
  };

  // Handler to generate and download canvas screenshot using Konva's toDataURL
  const handleGenerateScreenshot = () => {
    setIsTakingSnapshot(true);
    setIsSavingSnapshotLocal(true);
    setDebugRendering(false);
  };

  // Handler to save theme and screenshot to database
  const handleSaveThemeToDatabase = async () => {
    setIsSavingToDatabase(true);
    setIsSavingSnapshotLocal(false);
    setIsTakingSnapshot(true);
    setDebugRendering(false);
  };

  // Render settings based on selected element type
  const renderSettingsForSelectedElement = () => {
    if (!selectedElement) return (
      <div>Select an element to configure.</div>
    );

    const SettingsComponent = SettingsComponents[selectedElement.type];

    if (!SettingsComponent) return null;

    return (
      <div>Type: {selectedElement.type}
        <SettingsComponent
          element={selectedElement}
          updateElement={updateElement}
          distanceUnit={distanceUnit}
          temperatureUnit={temperatureUnit}
          backgroundColor={backgroundColor}
        />
      </div>
    );
  };

  useEffect(() => {
    const container = canvasContainerRef.current;
    if (!container) return;

    const updateScale = () => {
      const rect = container.getBoundingClientRect();
      const containerWidth = rect.width;
      const containerHeight = rect.height;

      // Calculate the scale factor
      const scaleFactor = Math.min(containerWidth / SCREEN_WIDTH, containerHeight / SCREEN_HEIGHT);

      setScale(scaleFactor); // Update visual scaling only
    };

    // Initial scale update
    updateScale();

    // Create a ResizeObserver to monitor size changes
    const resizeObserver = new ResizeObserver(() => {
      updateScale();
    });
    resizeObserver.observe(container);

    // Cleanup on unmount
    return () => {
      resizeObserver.unobserve(container);
    };
  }, [canvasContainerRef]);


  useEffect(() => {
    const takeSnapshot = async () => {
      if (isTakingSnapshot && !debugRendering) {
        try {
          const stage = stageRef.current.getStage();
          if (!stage) {
            toast.error('Stage reference not found.');
            setIsTakingSnapshot(false);
            setDebugRendering(true);
            return;
          }

          // Export to data URL
          const dataURL = stage.toDataURL({ pixelRatio: 1 / scale });

          // Extract Base64 string from dataURL
          const base64Image = dataURL.split(',')[1]; // Removes the 'data:image/png;base64,' part
          console.log("base64Image", base64Image);

          // If saving locally
          if (isSavingSnapshotLocal) {
            const link = document.createElement('a');
            link.href = dataURL;
            link.download = 'canvasScreenshot.png';
            document.body.appendChild(link);
            link.click();

            // Clean up
            document.body.removeChild(link);
            toast.success('Canvas screenshot generated successfully!');
          }

          // If saving to database, proceed to send data
          if (isSavingToDatabase) {
            // Serialize Theme Data
            const themeData = {
              elements,
              themeName,
              backgroundColor,
              mainColor,
              debugOverlaps,
              debugRandom,
              snapToGridEnabled,
              gridSize,
              scale,
              distanceUnit,
              temperatureUnit,
              themeType,
            };

            if (themeId === -1) {
              // Create new theme
              const response = await DatabaseService.createTheme(
                AuthService.getCurrentUser().email,
                themeName,
                themeType,
                themeData,
                base64Image // Pass Base64 string
              );

              if (response.status === 201) {
                toast.success('Theme and screenshot saved to the database successfully!');
                setThemeId(response.data[0].id);
                loadThemes();
              }
            } else {
              // Edit existing theme
              const response = await DatabaseService.editTheme(
                themeId,
                themeName,
                themeType,
                themeData,
                base64Image // Pass Base64 string
              );

              if (response.status === 204) {
                loadThemes();
                toast.success('Theme and screenshot saved to the database successfully!');
              }
            }
          }
        } catch (error) {
          console.error('Error during snapshot or saving:', error);
          toast.error(`Operation failed: ${error.message}`);
        } finally {
          // Re-enable debugRendering and reset snapshot states
          setDebugRendering(true);
          setIsTakingSnapshot(false);
          setIsSavingToDatabase(false);
          setIsSavingSnapshotLocal(false);
          loadThemes();
        }
      }
    }

    takeSnapshot();
  }, [
    isTakingSnapshot,
    debugRendering,
    scale,
    isSavingToDatabase,
    isSavingSnapshotLocal,
    elements,
    backgroundColor,
    mainColor,
    debugOverlaps,
    debugRandom,
    snapToGridEnabled,
    gridSize,
    distanceUnit,
    temperatureUnit,
    themeType
  ]);


  useEffect(() => {
    console.log('Theme ID:', themeId);
  }, [themeId]);

  return (
    <ErrorBoundary> {/* Wrap the entire Theme in ErrorBoundary */}
      <div style={{ display: 'flex', flexDirection: 'column', height: '90vh', overflow: 'hidden' }}>
        {/* Toast Notifications */}
        <ToastContainer />
        <PanelGroup direction="horizontal">
          <Panel
            collapsible={true}
            defaultSize={15}
            order={1}
          >
            <ThemeGrid
              currentUserEmail={AuthService.getCurrentUser().email}
              currentThemeOwnerEmail={currentThemeOwnerEmail}
              themeId={themeId}
              setThemeId={setThemeId}
              contentTheme={themesList}
              loadData={loadThemeData}
              removeData={deleteTheme}
              saveData={saveTheme}
              cloneData={cloneTheme}
              refreshDatas={loadThemes}
              createNew={createTheme}
              shareTheme={shareTheme}
              isAdmin={isAdmin}
              editAllowed={editAllowed}
            />
          </Panel>
          <ResizeHandle iconName={"fa fa-left-right"} />
          <Panel
            collapsible={true}
            defaultSize={55}
            order={1}
          >
            <>
              {/* Toolbar: Draggable Objects */}
              <Toolbar />

              {/* Middle Section: Canvas and Settings Side by Side */}
              <div style={{
                display: 'flex',
                flex: 1,
                overflow: 'hidden',
                height: '100%',
                marginLeft: '10px',
                marginRight: '10px',
              }}>
                {/* Canvas Area */}
                <div
                  ref={canvasContainerRef}
                  style={{
                    flex: 1,
                    // width: 'SCREEN_WIDTHpx',
                    // height: 'SCREEN_HEIGHTpx',
                    width: '100%',
                    height: '87%',
                    position: 'relative',
                  }}
                >
                  <CanvasArea
                    elements={elements}
                    stageSize={stageSize}
                    scale={scale}
                    showGrid={debugRendering}
                    gridColor={gridColor}
                    gridLineWidth={gridLineWidth}
                    gridOpacity={gridOpacity}
                    gridSize={gridSize} // Pass gridSize as a prop
                    renderElement={renderElement}
                    onDrop={handleDrop} // Passing handleDrop
                    onDragOver={handleDragOver} // Passing handleDragOver
                    stageRef={stageRef} // Passing stageRef as a prop
                    snapToGridEnabled={snapToGridEnabled} // Passing snapToGridEnabled
                    backgroundColor={backgroundColor} // Passing backgroundColor
                  />
                </div>
              </div>

            </>
          </Panel>

          <ResizeHandle iconName={"fa fa-left-right"} />
          {/* Settings Panel */}
          <Panel
            collapsible={true}
            defaultSize={10}
            order={1}
          >

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'auto',
              }}
            >
              {/* Object Settings */}
              <h3>Object Settings</h3>
              {renderSettingsForSelectedElement()}
              {selectedElement && (
                <button
                  onClick={deleteElement}
                  className='btn btn-danger' // Changed to 'btn-danger' for delete action
                  style={{ flex: 1, marginTop: '10px' }}
                  aria-label="Delete Element"
                >
                  Delete Element
                </button>
              )}
            </div>
          </Panel>
          <ResizeHandle iconName={"fa fa-left-right"} />
          {/* Global Settings Panel */}
          <Panel
            collapsible={true}
            defaultSize={10}
            order={1}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'auto',
              }}
            >

              <GlobalSettingsPanel
                themeName={themeName}
                setThemeName={setThemeName}
                backgroundColor={backgroundColor}
                setBackgroundColor={setBackgroundColor}
                mainColor={mainColor}
                setMainColor={setMainColor}
                distanceUnit={distanceUnit}
                setDistanceUnit={setDistanceUnit}
                temperatureUnit={temperatureUnit}
                setTemperatureUnit={setTemperatureUnit}
                setSnapToGridEnabled={setSnapToGridEnabled}
                setDebugOverlaps={setDebugOverlaps}
                setDebugRandom={setDebugRandom}
                setGridSize={setGridSize}
                setScale={setScale}
                themeType={themeType}
                setThemeType={setThemeType}
                elements={elements}
                debugOverlaps={debugOverlaps}
                debugRandom={debugRandom}
                snapToGridEnabled={snapToGridEnabled}
                gridSize={gridSize}
                scale={scale}
                stageRef={canvasContainerRef} // Pass the container ref for screenshot
              />

              {/* Brake Button */}
              <button
                onClick={toggleBrake}
                className={`btn ${isBrakeActive ? 'btn-primary' : 'btn-outline-primary'}`}
                style={{ flex: 1, marginTop: '10px' }}
                aria-label="Toggle Brake"
              >
                {isBrakeActive ? 'Deactivate Brake' : 'Activate Brake'}
              </button>

              {/* Debug Rendering Button */}
              <button
                onClick={toggleDebugRendering}
                className={`btn ${debugRendering ? 'btn-primary' : 'btn-outline-primary'}`}
                style={{ flex: 1, marginTop: '10px' }}
                aria-label="Toggle Debug Rendering"
              >
                {debugRendering ? 'Render final' : 'Render debug'}
              </button>


              {/* Import JSON Button */}
              <button
                onClick={() => fileInputRef.current.click()}
                className='btn btn-primary'
                style={{ flex: 1, marginTop: '10px' }}
                aria-label="Import Theme JSON"
              >
                Import JSON
              </button>
              <input
                type="file"
                accept="application/json"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleImportTheme}
              />

              {/* Export JSON Button */}
              <button
                onClick={handleExportTheme}
                className='btn btn-primary'
                style={{ flex: 1, marginTop: '10px' }}
                aria-label="Export Theme as JSON"
              >
                Export JSON
              </button>

              {/* Generate Screenshot Button */}
              <button
                onClick={handleGenerateScreenshot}
                className='btn btn-primary'
                style={{ flex: 1, marginTop: '10px' }}
                aria-label="Generate Canvas Screenshot"
              >
                Generate Screenshot
              </button>

              {/* Save Theme to Database Button */}
              <button
                onClick={handleSaveThemeToDatabase}
                className='btn btn-primary' // Ensure 'btn-purple' is defined in your CSS
                style={{ flex: 1, marginTop: '10px' }}
                aria-label="Save Theme to Database"
              >
                Save Theme to Database
              </button>

              {/* Reset Canvas Button */}
              <button
                onClick={handleResetCanvas}
                className='btn btn-primary'
                style={{ flex: 1, marginTop: '10px' }}
                aria-label="Reset Canvas"
              >
                Reset Canvas
              </button>

            </div>
          </Panel>
        </PanelGroup>
      </div>

    </ErrorBoundary >
  );
};

export default BoardTheme;
